import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ApiService } from './api-service';
import { ApiConstants } from '../../constants/common/api-constant';
import { IAppAboutContent, IAppAssessmentContent, IAppBookContent, IAppFooterContent, IAppHomeContent, IAppService } from '../../constants/interface/setting';
import { IServerError } from '../../constants/interface/common';

@Injectable({
  providedIn: 'root',
})
export class ContentService {
  homeContent = new BehaviorSubject<IAppHomeContent[] | null>(null);
  aboutContent = new BehaviorSubject<IAppAboutContent | null>(null);
  assessmentContent = new BehaviorSubject<IAppAssessmentContent[] | null>(null);
  footerContent = new BehaviorSubject<IAppFooterContent | null>(null);
  serviceContent = new BehaviorSubject<IAppService[] | null>(null);
  bookContent = new BehaviorSubject<IAppBookContent[] | null>(null);

  constructor(
    private apiService: ApiService,
    private messageService: MessageService
  ) {}

  getHomeContent(): void {
    this.apiService
      .ExecuteGet<IAppHomeContent[]>(this.apiService.baseUrl + ApiConstants.homeContent)
      .subscribe({
        next: (res: IAppHomeContent[]) => {
          this.homeContent.next(res ?? []);
        },
        error: ({ error }: IServerError) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.message || 'Failed to load home content. Please try again.'
          });
        }
      });
  }

  getAboutContent(): void {
    this.apiService
      .ExecuteGet<IAppAboutContent>(this.apiService.baseUrl + ApiConstants.aboutContent)
      .subscribe({
        next: (res: IAppAboutContent) => {
          this.aboutContent.next(res);
        },
        error: ({ error }: IServerError) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.message || 'Failed to load about content. Please try again.'
          });
        }
      });
  }

  getAssessmentContent(): void {
    this.apiService
      .ExecuteGet<IAppAssessmentContent[]>(this.apiService.baseUrl + ApiConstants.assessmentContent)
      .subscribe({
        next: (res: IAppAssessmentContent[]) => {
          this.assessmentContent.next(res ?? []);
        },
        error: ({ error }: IServerError) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.message || 'Failed to load assessment content. Please try again.'
          });
        }
      });
  }

  getFooterContent(): void {
    this.apiService
      .ExecuteGet<IAppFooterContent>(this.apiService.baseUrl + ApiConstants.footerContent)
      .subscribe({
        next: (res: IAppFooterContent) => {
          this.footerContent.next(res);
        },
        error: () => {
          // Silent failure: footer contact info is non-critical chrome.
        }
      });
  }

  getBookContent(): void {
    this.apiService
      .ExecuteGet<IAppBookContent[]>(this.apiService.baseUrl + ApiConstants.bookList)
      .subscribe({
        next: (res: IAppBookContent[]) => {
          const sorted = (res ?? []).slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
          this.bookContent.next(sorted);
        },
        error: ({ error }: IServerError) => {
          if (this.bookContent.value === null) {
            this.bookContent.next([]);
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.message || 'Failed to load book content. Please try again.'
          });
        }
      });
  }

  downloadFile(url: string, fileName?: string): Promise<void> {
    if (!url) return Promise.resolve();
    return fetch(url)
      .then(response => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName || this.fileNameFromUrl(url);
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        this.messageService.add({
          severity: 'error',
          summary: 'Download failed',
          detail: 'Unable to download the file. Please try again.'
        });
      });
  }

  private fileNameFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const last = pathname.split('/').pop();
      return last ? decodeURIComponent(last) : 'download';
    } catch {
      return 'download';
    }
  }

  private extensionFromUrl(url: string): string {
    try {
      const pathname = new URL(url).pathname;
      const last = pathname.split('/').pop() || '';
      const dot = last.lastIndexOf('.');
      return dot > -1 ? last.slice(dot) : '';
    } catch {
      return '';
    }
  }

  buildDownloadFileName(rawTitle: string | undefined, sourceUrl: string): string {
    const stripped = (rawTitle ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;|&#160;|&#xA0;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'");
    const cleaned = stripped
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return this.fileNameFromUrl(sourceUrl);
    const ext = this.extensionFromUrl(sourceUrl);
    return ext && !cleaned.toLowerCase().endsWith(ext.toLowerCase()) ? `${cleaned}${ext}` : cleaned;
  }

  getServiceContent(): void {
    this.apiService
      .ExecuteGet<IAppService[]>(this.apiService.baseUrl + ApiConstants.appService)
      .subscribe({
        next: (res: IAppService[]) => {
          this.serviceContent.next(res ?? []);
        },
        error: ({ error }: IServerError) => {
          // Unblock the loader on first-load failure but preserve any cached data.
          if (this.serviceContent.value === null) {
            this.serviceContent.next([]);
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error?.message || 'Failed to fetch services. Please try again.'
          });
        }
      });
  }
}
